import { useEffect, useRef, useState } from 'react'
import { BrowserQRCodeReader } from '@zxing/browser'
import { startCancellableScan, type ScannerControls } from '@/lib/cameraSession'
import { Card } from '@/components/ui'
import { useT } from '@/i18n/I18nProvider'

/**
 * A live camera view that reports the first QR code it reads.
 *
 * Extracted so the two things that scan — a machine sticker and a worker badge
 * — share one camera lifecycle. That lifecycle is the part worth not
 * duplicating: releasing the stream on unmount is what stops a phone burning
 * battery with the camera indicator lit after the user has navigated away.
 *
 * The caller decides what the text means. Both callers get a bare token out of
 * it, because a sticker may hold either a token or a full URL ending in one.
 */
export function QrScanner({
  onResult,
  onError,
  className,
}: {
  /** Called once, with whatever the code contained. */
  onResult: (text: string) => void
  onError?: (message: string) => void
  className?: string
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [ready, setReady] = useState(false)
  const t = useT()

  /*
   * Held in a ref rather than passed into the effect's dependency array: a
   * caller that defines its handler inline would otherwise restart the camera
   * on every render, which on a phone shows as the preview flickering.
   *
   * `t` rides along for the same reason. It is only read in the failure path,
   * and a language switch is no reason to tear down a live camera.
   */
  const handlers = useRef({ onResult, onError, t })
  handlers.current = { onResult, onError, t }

  useEffect(() => {
    const reader = new BrowserQRCodeReader()
    let stopped = false
    let controls: ScannerControls | null = null

    async function start() {
      try {
        /*
         * The cancellation is handled in `startCancellableScan`, and it has to
         * be. `decodeFromVideoDevice` resolves only once the user has answered
         * the permission prompt, while React's cleanup runs the moment the
         * screen closes — so the naive version assigned `controls` to a variable
         * cleanup had already read, and the stream arriving afterwards was never
         * stopped. On a phone that is the camera indicator staying lit with the
         * app closed.
         */
        controls = await startCancellableScan(
          () =>
            reader.decodeFromVideoDevice(undefined, videoRef.current!, (result) => {
              if (!result || stopped) return

              stopped = true
              controls?.stop()
              handlers.current.onResult(result.getText())
            }),
          () => stopped,
        )

        if (controls) setReady(true)
      } catch {
        handlers.current.onError?.(handlers.current.t('tasks.scanner.cameraBlocked'))
      }
    }

    void start()

    return () => {
      stopped = true
      controls?.stop()
    }
  }, [])

  return (
    <Card className={className ? `overflow-hidden p-0 ${className}` : 'overflow-hidden p-0'}>
      <video ref={videoRef} className="aspect-square w-full bg-black object-cover" muted playsInline />

      {!ready && (
        <p className="muted p-3 text-center text-xs">{t('tasks.scanner.starting')}</p>
      )}
    </Card>
  )
}

/**
 * The token out of a scanned code.
 *
 * Both the machine stickers and the worker badges encode a full URL so a
 * phone's own camera app opens the app directly; the last path segment is the
 * token. A bare token passes through unchanged.
 */
export function tokenFromScan(text: string): string {
  return text.split('?')[0].split('/').filter(Boolean).pop() ?? text
}
